import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  Lock,
  Download,
  Filter,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { weightAPI, companyAPI } from "../../services/api";

const PAGE_SIZE = 10;

const getImageUrl = (imagePath) => {
  if (!imagePath) return "https://via.placeholder.com/150";
  if (
    imagePath.startsWith("http://") ||
    imagePath.startsWith("https://") ||
    imagePath.startsWith("blob:")
  ) {
    return imagePath;
  }
  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:5000/backend";
  const serverOrigin = apiUrl.replace(/\/backend\/?$/, "");
  const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;

  // Route /uploads requests through /backend/uploads for live compatibility
  const finalPath = cleanPath.startsWith("/uploads/")
    ? `/backend${cleanPath}`
    : cleanPath;

  return `${serverOrigin}${finalPath}`;
};

export default function ClientHistory() {
  const { hasPermission } = useAuth();

  // Filters
  const [selectedVendor, setSelectedVendor] = useState("");
  const [resultFilter, setResultFilter] = useState("All");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);

  // Data
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  // Vendor list fetch
  useEffect(() => {
    companyAPI
      .list()
      .then((r) => setVendors(r.data || []))
      .catch(() => setVendors([]));
  }, []);

  // Entries fetch — filters badlale ki page 1 var ja
  const fetchEntries = useCallback(() => {
    if (!hasPermission("view_weight_history")) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const params = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    };
    if (selectedVendor) params.company_id = selectedVendor;
    if (resultFilter !== "All") params.result = resultFilter;
    if (fromDate) params.from = fromDate;
    if (toDate) params.to = toDate;

    weightAPI
      .list(params)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, selectedVendor, resultFilter, fromDate, toDate, hasPermission]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  // Filter badlala → page 1 reset
  const handleFilterChange = (setter) => (val) => {
    setter(val);
    setPage(1);
  };

  // Permission block
  if (!hasPermission("view_weight_history")) {
    return (
      <div
        style={{
          padding: 28,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <Lock size={40} color="var(--text3)" style={{ marginBottom: 12 }} />
          <h2 style={{ fontSize: 16, fontWeight: 600, color: "var(--text2)" }}>
            Access Restricted
          </h2>
          <p style={{ color: "var(--text3)", fontSize: 13, marginTop: 4 }}>
            You don't have permission to view weight history.
          </p>
        </div>
      </div>
    );
  }

  const entries = data?.entries || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Page numbers array (show max 5 pages around current)
  const getPageNumbers = () => {
    const pages = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  return (
    <div style={{ padding: 28 }}>
      {/* ── Header ─────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 20,
        }}
      >
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Weight History</h1>
          <p style={{ color: "var(--text2)", fontSize: 13, marginTop: 2 }}>
            {loading ? "Loading…" : `${total} entries found`}
          </p>
        </div>
        {/* {hasPermission('export_data') && (
          <button className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Download size={13} /> Export CSV
          </button>
        )} */}
      </div>

      {/* ── Filter Bar — dashboard sarkhaच ────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 16,
          padding: "10px 14px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          flexWrap: "wrap",
        }}
      >
        <Filter size={14} color="var(--text3)" />
        <span style={{ fontSize: 12, color: "var(--text3)", fontWeight: 500 }}>
          Filter:
        </span>

        {/* Vendor dropdown */}
        <select
          value={selectedVendor}
          onChange={(e) =>
            handleFilterChange(setSelectedVendor)(e.target.value)
          }
          style={{
            padding: "4px 10px",
            borderRadius: 6,
            border: "1px solid var(--border2)",
            background: "var(--surface)",
            color: "var(--text)",
            fontSize: 12,
            cursor: "pointer",
          }}
        >
          <option value="">All Companies</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>

        {/* Result filter */}
        {["All", "Pass", "Fail"].map((f) => (
          <button
            key={f}
            onClick={() => handleFilterChange(setResultFilter)(f)}
            style={{
              padding: "4px 12px",
              borderRadius: 6,
              fontSize: 12,
              border:
                resultFilter === f
                  ? "1px solid var(--accent)"
                  : "1px solid var(--border2)",
              background:
                resultFilter === f
                  ? "rgba(59,130,246,0.1)"
                  : f === "Pass" && resultFilter !== f
                    ? "rgba(16,185,129,0.05)"
                    : f === "Fail" && resultFilter !== f
                      ? "rgba(239,68,68,0.05)"
                      : "var(--surface)",
              color:
                resultFilter === f
                  ? "var(--accent)"
                  : f === "Pass"
                    ? "var(--success)"
                    : f === "Fail"
                      ? "var(--danger)"
                      : "var(--text2)",
              cursor: "pointer",
              fontWeight: resultFilter === f ? 600 : 400,
            }}
          >
            {f}
          </button>
        ))}

        {/* Date range */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            marginLeft: 4,
          }}
        >
          <span style={{ fontSize: 11, color: "var(--text3)" }}>From</span>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => handleFilterChange(setFromDate)(e.target.value)}
            style={{
              padding: "3px 8px",
              borderRadius: 6,
              border: "1px solid var(--border2)",
              background: "var(--surface)",
              color: "var(--text)",
              fontSize: 12,
            }}
          />
          <span style={{ fontSize: 11, color: "var(--text3)" }}>To</span>
          <input
            type="date"
            value={toDate}
            onChange={(e) => handleFilterChange(setToDate)(e.target.value)}
            style={{
              padding: "3px 8px",
              borderRadius: 6,
              border: "1px solid var(--border2)",
              background: "var(--surface)",
              color: "var(--text)",
              fontSize: 12,
            }}
          />
        </div>

        {/* Clear filters */}
        {(selectedVendor || resultFilter !== "All" || fromDate || toDate) && (
          <button
            onClick={() => {
              setSelectedVendor("");
              setResultFilter("All");
              setFromDate("");
              setToDate("");
              setPage(1);
            }}
            style={{
              padding: "4px 10px",
              borderRadius: 6,
              fontSize: 11,
              border: "1px solid var(--border2)",
              background: "transparent",
              color: "var(--text3)",
              cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            ✕ Clear
          </button>
        )}
      </div>

      {/* ── Table ──────────────────────────────────────────── */}
      <div
        className="card"
        style={{
          padding: 0,
          overflow: "hidden",
          marginBottom: 14,
          alignItems: "center",
        }}
      >
        <table>
          <thead>
            <tr>
              <th>Part ID</th>
              <th>Part</th>
              <th>Weight (g)</th>
              <th>Image</th>
              <th>Result</th>
              <th>Serial Number</th>
              <th>Date</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    textAlign: "center",
                    padding: 32,
                    color: "var(--text3)",
                    fontSize: 12,
                  }}
                >
                  Loading…
                </td>
              </tr>
            )}
            {!loading && entries.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{
                    textAlign: "center",
                    padding: 32,
                    color: "var(--text3)",
                    fontSize: 12,
                  }}
                >
                  No records found for selected filters
                </td>
              </tr>
            )}
            {!loading &&
              entries.map((h, i) => (
                <tr key={h.id}>
                  <td className="mono text3" style={{ fontSize: 11 }}>
                    {h.product_code || "—"}
                  </td>
                  <td>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>
                      {h.product_name}
                    </div>
                  </td>
                  <td
                    style={{
                      fontFamily: "var(--mono)",
                      fontWeight: 600,
                      color:
                        h.result === "Pass"
                          ? "var(--success)"
                          : "var(--danger)",
                    }}
                  >
                    {h.weight}g
                  </td>
                  <td>
                    <img
                      src={getImageUrl(h.product_image)}
                      alt="Part"
                      style={{
                        width: 50,
                        height: 50,
                        borderRadius: 6,
                        objectFit: "cover",
                      }}
                    />
                  </td>
                  <td>
                    <span
                      className={`badge ${h.result === "Pass" ? "badge-success" : "badge-danger"}`}
                    >
                      {h.result}
                    </span>
                  </td>
                  <td className="mono text2" style={{ fontSize: 11 }}>
                    {h.serial_number || "—"}
                  </td>
                  <td className="text3" style={{ fontSize: 11 }}>
                    {new Date(h.recorded_at).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="text3" style={{ fontSize: 11 }}>
                    {new Date(h.recorded_at)
                      .toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })
                      .replace(/\s*[APap][Mm]/g, "")}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ── Pagination ─────────────────────────────────────── */}
      {totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 4px",
          }}
        >
          {/* Entry count info */}
          <span style={{ fontSize: 12, color: "var(--text3)" }}>
            Showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, total)} of {total} entries
          </span>

          {/* Page buttons */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {/* Prev */}
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface)",
                color: page === 1 ? "var(--text3)" : "var(--text)",
                cursor: page === 1 ? "not-allowed" : "pointer",
                fontSize: 12,
                opacity: page === 1 ? 0.5 : 1,
              }}
            >
              <ChevronLeft size={14} />
            </button>

            {/* First page + ellipsis */}
            {getPageNumbers()[0] > 1 && (
              <>
                <button onClick={() => setPage(1)} style={pageBtn(page === 1)}>
                  1
                </button>
                {getPageNumbers()[0] > 2 && (
                  <span
                    style={{
                      color: "var(--text3)",
                      fontSize: 12,
                      padding: "0 4px",
                    }}
                  >
                    …
                  </span>
                )}
              </>
            )}

            {/* Page numbers */}
            {getPageNumbers().map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                style={pageBtn(page === p)}
              >
                {p}
              </button>
            ))}

            {/* Last page + ellipsis */}
            {getPageNumbers().at(-1) < totalPages && (
              <>
                {getPageNumbers().at(-1) < totalPages - 1 && (
                  <span
                    style={{
                      color: "var(--text3)",
                      fontSize: 12,
                      padding: "0 4px",
                    }}
                  >
                    …
                  </span>
                )}
                <button
                  onClick={() => setPage(totalPages)}
                  style={pageBtn(page === totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}

            {/* Next */}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              style={{
                display: "flex",
                alignItems: "center",
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid var(--border2)",
                background: "var(--surface)",
                color: page === totalPages ? "var(--text3)" : "var(--text)",
                cursor: page === totalPages ? "not-allowed" : "pointer",
                fontSize: 12,
                opacity: page === totalPages ? 0.5 : 1,
              }}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Page button style helper
function pageBtn(isActive) {
  return {
    minWidth: 32,
    padding: "5px 8px",
    borderRadius: 6,
    fontSize: 12,
    border: isActive ? "1px solid var(--accent)" : "1px solid var(--border2)",
    background: isActive ? "rgba(59,130,246,0.15)" : "var(--surface)",
    color: isActive ? "var(--accent)" : "var(--text2)",
    cursor: isActive ? "default" : "pointer",
    fontWeight: isActive ? 700 : 400,
  };
}
